/**
 * @bos/portal3 — the authenticated app shell: a branded sticky header (Sorvel seal +
 * wordmark, Dashboard / Account nav, the signed-in user + Log out), a centered content
 * container the routed page renders into, and a quiet footer with the liveness pill.
 *
 * The shell also GATES access: it is the single layout wrapping every protected page, so
 * the auth check lives here rather than being repeated per route. While the session is
 * still being probed it shows a calm "checking" screen; an anonymous visitor is sent to
 * the sign-in entry (remembering where they were headed). Using react-router <NavLink>/
 * <Outlet> keeps navigation client-side and addressable — the URL changes, Back/Forward/
 * Refresh all work, and the header never unmounts.
 */
import { NavLink, Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Brand } from './components/Brand';
import { ServerStatus } from './components/ServerStatus';
import { PATHS } from './routes';
import { useAuth } from './auth/AuthContext';

export function AppShell() {
  const { status, user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  if (status === 'loading') {
    return (
      <div className="centered" aria-busy="true">
        <div className="centered__inner">
          <Brand size="lg" />
          <p className="centered__lede">Checking your session…</p>
        </div>
      </div>
    );
  }

  if (status === 'anonymous') {
    return <Navigate to={PATHS.login} replace state={{ from: location.pathname }} />;
  }

  async function onLogout() {
    // Leave for the sign-in entry before clearing the session, so a protected page doesn't
    // briefly flip through its own redirect as the auth state changes.
    navigate(PATHS.login, { replace: true });
    await logout();
  }

  return (
    <div className="shell">
      <header className="shell__header">
        <div className="shell__header-inner">
          <Brand to={PATHS.dashboard} />
          <nav className="shell__nav" aria-label="Primary">
            <NavLink to={PATHS.dashboard} className="navlink">
              Dashboard
            </NavLink>
            <NavLink to={PATHS.account} className="navlink">
              Account
            </NavLink>
            {user?.role === 'admin' && (
              // A courtesy, not the guard: the server refuses non-admins on every admin
              // route regardless of what this header shows.
              <NavLink to={PATHS.admin} className="navlink">
                Admin
              </NavLink>
            )}
            <span className="shell__who">
              <span className="shell__whoami">{user?.displayName || user?.email}</span>
              <button type="button" className="btn btn--ghost btn--sm" onClick={onLogout}>
                Log out
              </button>
            </span>
          </nav>
        </div>
      </header>

      <main className="shell__main">
        <Outlet />
      </main>

      <footer className="shell__footer">
        <div className="shell__footer-inner">
          <span>Sorvel — a calm home for your business</span>
          <ServerStatus />
        </div>
      </footer>
    </div>
  );
}
